import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  User, Briefcase, Award, Globe, Mail, Phone, Linkedin, Twitter,
  Building2, Calendar, MapPin, Play, ExternalLink, ChevronRight,
  Lightbulb, BookOpen, Trophy, Target, Edit, Loader2, Video,
  MessageCircle, Copy, Check, X, Stethoscope, Scale, GraduationCap,
  Mic, DollarSign, Code, Palette, Flag, Users, Sparkles, Link2,
  Megaphone, CalendarDays, FolderOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png";

// Personal Profile Category Configuration
const PERSONAL_CATEGORY_CONFIG = {
  board_level: {
    label: 'Board Level Executive',
    bgClass: 'bg-[#1E3A5F]/20 border-[#1E3A5F]/30 text-blue-300',
    icon: Briefcase,
    kpiLabels: { years: 'Years on Boards', count1: 'Companies Advised', count2: 'Industries' },
    cta: 'Connect for Board Role'
  },
  founder: {
    label: 'Founder / Entrepreneur',
    bgClass: 'bg-orange-500/20 border-orange-500/30 text-orange-300',
    icon: Sparkles,
    kpiLabels: { years: 'Years Experience', count1: 'Companies Founded', count2: 'Exits' },
    cta: 'Explore Collaboration'
  },
  diplomatic: {
    label: 'Diplomatic / Government',
    bgClass: 'bg-amber-500/20 border-amber-500/30 text-amber-300',
    icon: Flag,
    kpiLabels: { years: 'Years in Service', count1: 'Countries Served', count2: 'Agreements' },
    cta: 'Request Official Meeting'
  },
  consultant: {
    label: 'Consultant / Advisory',
    bgClass: 'bg-blue-500/20 border-blue-500/30 text-blue-300',
    icon: Users,
    kpiLabels: { years: 'Years Consulting', count1: 'Projects Delivered', count2: 'Clients' },
    cta: 'Book Consultation'
  },
  ca_finance: {
    label: 'CA / Finance Professional',
    bgClass: 'bg-green-500/20 border-green-500/30 text-green-300',
    icon: DollarSign,
    kpiLabels: { years: 'Years Practice', count1: 'Clients Managed', count2: 'Audits' },
    cta: 'Schedule Financial Review'
  },
  medical: {
    label: 'Medical Professional',
    bgClass: 'bg-teal-500/20 border-teal-500/30 text-teal-300',
    icon: Stethoscope,
    kpiLabels: { years: 'Years Experience', count1: 'Procedures', count2: 'Publications' },
    cta: 'Book Appointment'
  },
  legal: {
    label: 'Legal Professional',
    bgClass: 'bg-rose-500/20 border-rose-500/30 text-rose-300',
    icon: Scale,
    kpiLabels: { years: 'Years in Practice', count1: 'Cases', count2: 'Jurisdictions' },
    cta: 'Request Legal Consultation'
  },
  academic: {
    label: 'Academic / Researcher',
    bgClass: 'bg-purple-500/20 border-purple-500/30 text-purple-300',
    icon: GraduationCap,
    kpiLabels: { years: 'Years Academic', count1: 'Publications', count2: 'Citations' },
    cta: 'Invite to Speak'
  },
  creative: {
    label: 'Creative / Media',
    bgClass: 'bg-pink-500/20 border-pink-500/30 text-pink-300',
    icon: Palette,
    kpiLabels: { years: 'Years Experience', count1: 'Projects', count2: 'Awards' },
    cta: 'Discuss Project'
  },
  tech_leader: {
    label: 'Technology Leader',
    bgClass: 'bg-cyan-500/20 border-cyan-500/30 text-cyan-300',
    icon: Code,
    kpiLabels: { years: 'Years in Tech', count1: 'Products Built', count2: 'Patents' },
    cta: 'Explore Tech Partnership'
  },
  investor: {
    label: 'Investor / VC',
    bgClass: 'bg-amber-500/20 border-amber-500/30 text-amber-300',
    icon: DollarSign,
    kpiLabels: { years: 'Years Investing', count1: 'Investments', count2: 'Exits' },
    cta: 'Pitch Your Startup'
  },
  speaker: {
    label: 'Speaker / Thought Leader',
    bgClass: 'bg-[#C41E3A]/20 border-[#C41E3A]/30 text-rose-300',
    icon: Mic,
    kpiLabels: { years: 'Years Speaking', count1: 'Keynotes', count2: 'Followers' },
    cta: 'Book for Event'
  },
  general: {
    label: 'Professional',
    bgClass: 'bg-zinc-700/20 border-zinc-600/30 text-zinc-300',
    icon: User,
    kpiLabels: { years: 'Years Experience', count1: 'Clients', count2: 'Projects' },
    cta: 'Get in Touch'
  }
};

// Extract YouTube video ID
function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
}

// Bento Video Card
function BentoVideoCard({ video, size = 'default', onPlay }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const youtubeId = getYouTubeId(video?.url);
  
  if (!video?.url) return null;
  
  // Use BrandStage branded covers instead of YouTube thumbnails
  const coverImage = size === 'hero' 
    ? '/images/hero-video-cover.jpg' 
    : '/images/secondary-video-cover.jpg';
  
  const handlePlay = () => {
    setIsPlaying(true);
    if (onPlay) onPlay();
  };
  
  return (
    <div className={`relative group rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-zinc-600 transition-all duration-300 aspect-video`}>
      {!isPlaying ? (
        <div 
          className="relative w-full h-full cursor-pointer"
          onClick={handlePlay}
          data-testid="video-card-thumbnail"
        >
          {/* BrandStage Branded Cover Image */}
          <img 
            src={coverImage}
            alt={video.title || 'Video'}
            className="w-full h-full object-cover"
          />
          
          {/* Title overlay at bottom */}
          {video.title && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
              <p className="text-white font-medium text-sm line-clamp-2">{video.title}</p>
            </div>
          )}
        </div>
      ) : (
        youtubeId ? (
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?rel=0&autoplay=1`}
            title={video.title || 'Video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-zinc-800">
            <a href={video.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-2 text-zinc-400 hover:text-white">
              <Play className="w-12 h-12" />
              <span className="text-sm">Open Video</span>
            </a>
          </div>
        )
      )}
    </div>
  );
}

// Testimonial Card
function TestimonialCard({ testimonial }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const youtubeId = getYouTubeId(testimonial?.video_url);

  return (
    <div className="relative group rounded-xl overflow-hidden bg-zinc-900/50 border border-zinc-800 hover:border-[#C41E3A]/50 transition-all duration-300" data-testid="testimonial-card">
      <div className="aspect-[4/3] relative">
        {!isPlaying ? (
          <div className="w-full h-full cursor-pointer relative" onClick={() => setIsPlaying(true)}>
            {youtubeId ? (
              <img src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`} alt={testimonial.contact_name} className="w-full h-full object-cover" />
            ) : testimonial.video_url ? (
              <video src={testimonial.video_url} className="w-full h-full object-cover" muted />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                <User className="w-16 h-16 text-zinc-600" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20 group-hover:bg-[#C41E3A] group-hover:border-[#C41E3A] transition-all duration-300">
                <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
              </div>
            </div>
          </div>
        ) : (
          youtubeId ? (
            <iframe src={`https://www.youtube.com/embed/${youtubeId}?rel=0&autoplay=1`} title={testimonial.contact_name} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
          ) : (
            <video src={testimonial.video_url} className="w-full h-full object-cover" controls autoPlay />
          )
        )}
      </div>
      <div className="p-4">
        <p className="text-white font-semibold">{testimonial.contact_name}</p>
        <p className="text-zinc-400 text-sm">{testimonial.contact_company || 'Colleague'}</p>
        {testimonial.message && (
          <p className="text-zinc-500 text-xs mt-2 line-clamp-2 italic">&quot;{testimonial.message}&quot;</p>
        )}
      </div>
    </div>
  );
}

// KPI Card
function KPICard({ value, label, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center hover:border-zinc-700 transition-colors" data-testid="kpi-card">
      <div className="flex items-center justify-center mb-2">
        <Icon className="w-5 h-5 text-[#C41E3A]" />
      </div>
      <div className="text-2xl md:text-3xl font-bold text-white">{value}</div>
      <div className="text-xs text-zinc-500 uppercase tracking-wide mt-1">{label}</div>
    </div>
  );
}

// Smart KPI Row - adapts grid based on how many KPIs exist
function KPIRow({ kpis }) {
  const validKPIs = kpis.filter(k => k.value);
  if (validKPIs.length === 0) return null;
  
  const gridClass = validKPIs.length === 1 
    ? 'grid-cols-1 max-w-xs mx-auto' 
    : validKPIs.length === 2 
    ? 'grid-cols-2 max-w-md mx-auto'
    : validKPIs.length === 3
    ? 'grid-cols-3'
    : 'grid-cols-2 md:grid-cols-4';
  
  return (
    <div className={`col-span-12 grid ${gridClass} gap-3`}>
      {validKPIs.map((kpi, idx) => (
        <KPICard key={idx} value={kpi.value} label={kpi.label} icon={kpi.icon} />
      ))}
    </div>
  );
}

// Category Badge
function CategoryBadge({ category }) {
  const config = PERSONAL_CATEGORY_CONFIG[category] || PERSONAL_CATEGORY_CONFIG.general;
  const Icon = config.icon;
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${config.bgClass}`} data-testid="category-badge">
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </div>
  );
}

export default function PublicLeadershipProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [showcaseOpps, setShowcaseOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [linkedCompany, setLinkedCompany] = useState(null);
  const [copied, setCopied] = useState(false);

  const isOwner = user && profile && (
    user._id === profile.created_by || 
    user.id === profile.created_by ||
    user.email?.toLowerCase() === 'brandstagevideo@gmail.com'
  );

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/leadership-profiles/slug/${slug}`);
        setProfile(response.data);
        
        // Track page view
        if (response.data.id) {
          axios.post(`${API_URL}/api/profiles/${response.data.id}/track?event_type=view`).catch(() => {});
        }
        
        // Fetch linked company
        if (response.data.linked_company_profile) {
          try {
            const companyRes = await axios.get(`${API_URL}/api/profiles/${response.data.linked_company_profile}`);
            setLinkedCompany(companyRes.data);
          } catch (e) {
            console.log('Linked company not found');
          }
        }
        
        // Fetch testimonials
        if (response.data.id) {
          try {
            const testimonialsRes = await axios.get(`${API_URL}/api/leadership-profiles/${response.data.id}/testimonials`);
            setTestimonials(testimonialsRes.data.testimonials || []);
          } catch (e) {
            console.log('No testimonials found');
          }
        }
        
        // Fetch opportunities linked to this profile
        if (response.data.slug) {
          try {
            const oppsRes = await axios.get(`${API_URL}/api/connect/profile/leadership/${response.data.slug}/opportunities`);
            if (oppsRes.data.show_on_profile) {
              setOpportunities(oppsRes.data.opportunities || []);
            }
          } catch (e) {
            console.log('No opportunities found');
          }
          // Fetch showcase opportunity pages
          try {
            const soRes = await axios.get(`${API_URL}/api/p/${response.data.slug}/opportunities`);
            setShowcaseOpps(soRes.data.opportunities || []);
          } catch (e) {
            console.log('No showcase opportunities found');
          }
        }
      } catch (err) {
        setError('Profile not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [slug]);

  const shareUrl = window.location.href;
  const shareText = profile ? `Check out ${profile.leader_name} on BrandStage` : '';
  
  // Analytics tracking helper
  const trackEvent = (eventType) => {
    if (profile?.id) {
      axios.post(`${API_URL}/api/profiles/${profile.id}/track?event_type=${eventType}`).catch(() => {});
    }
  };
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      trackEvent('share_click');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  const handleWhatsAppClick = () => {
    trackEvent('whatsapp_click');
    trackEvent('contact_click');
  };
  
  const handleEmailClick = () => {
    trackEvent('email_click');
    trackEvent('contact_click');
  };
  
  const handleLinkedInClick = () => {
    trackEvent('share_click');
  };
  
  const handleVideoPlay = () => {
    trackEvent('video_play');
  };
  
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const emailUrl = profile ? `mailto:${profile.contact_email}?subject=Connection Request - ${profile.leader_name}` : '';

  // Get category config
  const categoryConfig = PERSONAL_CATEGORY_CONFIG[profile?.profile_category] || PERSONAL_CATEGORY_CONFIG.general;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C41E3A]" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Profile Not Found</h1>
          <Link to="/" className="text-[#C41E3A] hover:underline">← Back to Home</Link>
        </div>
      </div>
    );
  }

  const isCompanyLeadership = profile.leadership_mode === 'company_leadership';
  const visibleTestimonials = testimonials.filter(t => t.status === 'approved' && t.is_visible);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Floating Header */}
      <header className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-5xl">
        <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-full px-4 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img src={LOGO_URL} alt="BrandStage" className="h-7 w-auto rounded" />
          </Link>
          
          <div className="flex items-center gap-2">
            <button
              onClick={copyLink}
              className={`p-2 rounded-full transition-colors ${copied ? 'bg-green-600 text-white' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'}`}
              data-testid="copy-link-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
            <a href={whatsappUrl} onClick={handleWhatsAppClick} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-green-600 hover:bg-green-500 text-white transition-colors" data-testid="whatsapp-share-btn">
              <MessageCircle className="w-4 h-4" />
            </a>
            <a href={linkedinUrl} onClick={handleLinkedInClick} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-[#0077B5] hover:bg-[#0088CC] text-white transition-colors" data-testid="linkedin-share-btn">
              <Linkedin className="w-4 h-4" />
            </a>
            {isOwner && (
              <button
                onClick={() => navigate(`/leadership/edit/${profile.id}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C41E3A] hover:bg-[#A01830] text-white text-sm font-medium transition-colors"
                data-testid="edit-profile-btn"
              >
                <Edit className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 pt-24 pb-12">
        {/* BENTO GRID - Hero Section */}
        <div className="grid grid-cols-12 gap-3 md:gap-4 mb-6" data-testid="bento-hero-grid">
          {/* Hero Video Card - Adapts when no video */}
          {(profile.hero_video?.url || (profile.videos && profile.videos[0]?.url)) ? (
            <div className="col-span-12 md:col-span-7 order-2 md:order-1">
              <BentoVideoCard video={profile.hero_video?.url ? profile.hero_video : profile.videos[0]} size="hero" />
            </div>
          ) : (
            <div className="col-span-12 md:col-span-7 order-2 md:order-1 hidden md:block" />
          )}
          
          {/* Profile Info Card - Expands when no video */}
          <div className={`col-span-12 ${(profile.hero_video?.url || (profile.videos && profile.videos[0]?.url)) ? 'md:col-span-5' : 'md:col-span-12'} order-1 md:order-2 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 md:p-6`}>
            {/* Category Badge - Only show if profile has a category set */}
            {profile.profile_category && (
              <div className="mb-4">
                <CategoryBadge category={profile.profile_category} />
              </div>
            )}
            
            {/* Photo & Name */}
            <div className="flex items-start gap-4 mb-4">
              {profile.leader_photo || profile.photo_url ? (
                <img 
                  src={profile.leader_photo || profile.photo_url} 
                  alt={profile.leader_name}
                  className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover border border-zinc-700"
                  data-testid="leader-photo"
                />
              ) : (
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-[#C41E3A] to-[#8a1428] flex items-center justify-center border border-zinc-700">
                  <span className="text-3xl font-bold text-white">
                    {profile.leader_name?.charAt(0) || 'L'}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-white leading-tight" data-testid="leader-name">
                  {profile.leader_name}
                </h1>
                <p className="text-[#C41E3A] font-medium text-sm mt-1">{profile.leader_title}</p>
                {profile.company_name && (
                  <div className="flex items-center gap-1.5 text-zinc-400 text-xs mt-2">
                    <Building2 className="w-3.5 h-3.5" />
                    {profile.company_name}
                  </div>
                )}
              </div>
            </div>
            
            {/* Mode Badge */}
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-4 ${
              isCompanyLeadership 
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
            }`}>
              {isCompanyLeadership ? 'Company Leader' : 'Thought Leader'}
            </div>
            
            {/* Linked Company */}
            {linkedCompany && (
              <Link 
                to={`/p/${linkedCompany.slug}`}
                className="flex items-center gap-2 text-[#C41E3A] hover:underline text-sm mb-4"
              >
                View Company Profile <ExternalLink className="w-3 h-3" />
              </Link>
            )}
            
            {/* Quick Stats */}
            <div className="flex flex-wrap gap-2 text-xs text-zinc-500">
              {profile.years_experience && (
                <span className="flex items-center gap-1 bg-zinc-800/50 px-2.5 py-1 rounded-full">
                  <Calendar className="w-3 h-3" />
                  {profile.years_experience}
                </span>
              )}
              {profile.industries && profile.industries[0] && (
                <span className="flex items-center gap-1 bg-zinc-800/50 px-2.5 py-1 rounded-full">
                  <Briefcase className="w-3 h-3" />
                  {profile.industries[0]}
                </span>
              )}
            </div>
          </div>
          
          {/* KPI Stats Row - Smart responsive */}
          <KPIRow kpis={[
            { value: profile.years_experience, label: categoryConfig.kpiLabels.years, icon: Calendar },
            { value: profile.companies_count || profile.clients_count, label: categoryConfig.kpiLabels.count1, icon: Building2 },
            { value: profile.industries?.length, label: "Industries", icon: Briefcase },
            { value: profile.awards?.length || profile.keynotes_count, label: categoryConfig.kpiLabels.count2, icon: Trophy }
          ]} />
        </div>

        {/* BENTO GRID - About Section */}
        <div className="grid grid-cols-12 gap-3 md:gap-4 mb-6" data-testid="bento-about-grid">
          {/* Executive Summary */}
          {profile.profile_sections?.executive_summary && (
            <div className="col-span-12 bg-gradient-to-br from-[#1E3A5F]/20 to-zinc-900 border border-[#1E3A5F]/30 rounded-2xl p-5 md:p-6">
              <p className="text-lg md:text-xl text-zinc-200 leading-relaxed font-light italic">
                &quot;{profile.profile_sections.executive_summary}&quot;
              </p>
            </div>
          )}
          
          {/* Who I Am */}
          {profile.profile_sections?.who_i_am && (
            <div className="col-span-12 md:col-span-6 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
                <User className="w-4 h-4 text-[#C41E3A]" />
                Who I Am
              </h2>
              <p className="text-zinc-300 leading-relaxed text-sm">{profile.profile_sections.who_i_am}</p>
            </div>
          )}
          
          {/* My Journey */}
          {profile.profile_sections?.my_journey && (
            <div className="col-span-12 md:col-span-6 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
                <Briefcase className="w-4 h-4 text-[#C41E3A]" />
                My Journey
              </h2>
              <p className="text-zinc-300 leading-relaxed text-sm">{profile.profile_sections.my_journey}</p>
            </div>
          )}
          
          {/* My Expertise */}
          {profile.profile_sections?.my_expertise && (
            <div className="col-span-12 md:col-span-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
                <Award className="w-4 h-4 text-[#C41E3A]" />
                My Expertise
              </h2>
              <p className="text-zinc-300 leading-relaxed text-sm">{profile.profile_sections.my_expertise}</p>
              
              {/* Expertise Tags */}
              {profile.expertise_areas && profile.expertise_areas.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {profile.expertise_areas.map((area, idx) => (
                    <span key={idx} className="bg-[#C41E3A]/10 text-[#C41E3A] px-3 py-1 rounded-full text-xs font-medium">
                      {area}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* My Vision */}
          {profile.profile_sections?.my_vision && (
            <div className="col-span-12 md:col-span-4 bg-[#1E3A5F]/10 border border-[#1E3A5F]/30 rounded-2xl p-5">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
                <Lightbulb className="w-4 h-4 text-[#C41E3A]" />
                My Vision
              </h2>
              <p className="text-zinc-300 leading-relaxed text-sm">{profile.profile_sections.my_vision}</p>
            </div>
          )}
          
          {/* Achievements & Awards */}
          {(profile.achievements || (profile.awards && profile.awards.length > 0)) && (
            <div className="col-span-12 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
              <h2 className="text-sm font-bold text-white mb-3 flex items-center gap-2 uppercase tracking-wide">
                <Trophy className="w-4 h-4 text-[#C41E3A]" />
                Achievements & Awards
              </h2>
              {profile.achievements && (
                <p className="text-zinc-300 leading-relaxed text-sm mb-3">{profile.achievements}</p>
              )}
              {profile.awards && profile.awards.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {profile.awards.map((award, idx) => (
                    <span key={idx} className="bg-amber-500/10 text-amber-400 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                      <Trophy className="w-3 h-3" />
                      {award}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Published Articles & Media */}
          {profile.published_articles && profile.published_articles.length > 0 && (
            <div className="col-span-12 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
              <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
                <BookOpen className="w-4 h-4 text-[#C41E3A]" />
                Publications & Media
              </h2>
              <div className="space-y-3">
                {/* Group by category */}
                {Object.entries(
                  profile.published_articles.reduce((acc, article) => {
                    const cat = article.category || 'other';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(article);
                    return acc;
                  }, {})
                ).map(([category, articles]) => (
                  <div key={category}>
                    <p className="text-zinc-500 text-xs uppercase tracking-wide mb-2">
                      {category === 'blog' ? 'Blog Posts' :
                       category === 'news' ? 'News Articles' :
                       category === 'announcement' ? 'Announcements' :
                       category === 'product_launch' ? 'Product Launches' :
                       category === 'speech' ? 'Speeches & Talks' :
                       category === 'podcast' ? 'Podcast Appearances' :
                       category === 'interview' ? 'Published Interviews' :
                       category === 'award' ? 'Awards & Recognition' :
                       'Other'}
                    </p>
                    <div className="space-y-2">
                      {articles.map((article, idx) => (
                        <a
                          key={idx}
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 hover:border-zinc-600 rounded-lg p-3 transition-colors group"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-white text-sm font-medium truncate group-hover:text-[#C41E3A] transition-colors">
                                {article.title}
                              </p>
                              {article.publication && (
                                <p className="text-zinc-500 text-xs mt-0.5">{article.publication}</p>
                              )}
                            </div>
                            <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-[#C41E3A] flex-shrink-0 transition-colors" />
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Fallback for profiles without sections */}
          {!profile.profile_sections && profile.tagline && (
            <div className="col-span-12 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
              <p className="text-zinc-300 leading-relaxed italic">&quot;{profile.tagline}&quot;</p>
            </div>
          )}
        </div>

        {/* TESTIMONIALS SECTION */}
        {visibleTestimonials.length > 0 && (
          <div className="mb-6" data-testid="testimonials-section">
            <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
              <MessageCircle className="w-4 h-4 text-[#C41E3A]" />
              Testimonials
            </h2>
            <div className={`grid gap-4 ${
              visibleTestimonials.length === 1 
                ? 'grid-cols-1 max-w-md' 
                : visibleTestimonials.length === 2 
                ? 'grid-cols-1 md:grid-cols-2 max-w-2xl'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {visibleTestimonials.map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          </div>
        )}

        {/* VIDEOS GALLERY */}
        {((profile.videos && profile.videos.filter(v => v.url).length > 1) || (profile.additional_videos && profile.additional_videos.filter(v => v.url).length > 0)) && (
          <div className="mb-6" data-testid="videos-section">
            <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
              <Video className="w-4 h-4 text-[#C41E3A]" />
              Featured Videos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Show additional_videos if available, otherwise fall back to profile.videos */}
              {(profile.additional_videos && profile.additional_videos.filter(v => v.url).length > 0) 
                ? profile.additional_videos.filter(v => v.url).map((video, idx) => (
                    <BentoVideoCard key={idx} video={video} size="small" />
                  ))
                : profile.videos?.filter(v => v.url).slice(1).map((video, idx) => (
                    <BentoVideoCard key={idx} video={video} size="small" />
                  ))
              }
            </div>
          </div>
        )}

        {/* CONTACT SECTION */}
        <div className="grid grid-cols-12 gap-3 md:gap-4" data-testid="bento-contact-grid">
          {/* Thought Leadership Topics */}
          {/* Thought Leadership Topics */}
          {profile.thought_leadership_topics && profile.thought_leadership_topics.length > 0 && (
            <div className="col-span-12 md:col-span-7 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5">
              <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
                <BookOpen className="w-4 h-4 text-[#C41E3A]" />
                Thought Leadership Topics
              </h2>
              <div className="flex flex-wrap gap-2">
                {profile.thought_leadership_topics.map((topic, idx) => (
                  <span key={idx} className="bg-purple-500/10 text-purple-400 px-3 py-1.5 rounded-lg text-sm font-medium">
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Animated Opportunity Banner */}
          {showcaseOpps.length > 0 && (
            <div className="col-span-12" data-testid="opportunity-notification-banner">
              <Link
                to={`/p/${slug}/opportunity/${showcaseOpps[0].slug}`}
                className="block bg-gradient-to-r from-[#C41E3A]/10 via-[#C41E3A]/5 to-[#C41E3A]/10 border border-[#C41E3A]/30 rounded-2xl px-5 py-3.5 hover:border-[#C41E3A]/50 transition-all group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-[#C41E3A]/5 animate-pulse" />
                <div className="relative flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#C41E3A]/20 flex items-center justify-center shrink-0">
                    <Megaphone className="w-4 h-4 text-[#C41E3A] animate-bounce" style={{ animationDuration: '2s' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#C41E3A] text-xs font-bold uppercase tracking-wider">New Opportunity Posted</p>
                    <p className="text-white text-sm font-semibold truncate group-hover:text-[#C41E3A] transition-colors">{showcaseOpps[0].title}</p>
                  </div>
                  <span className="text-[#C41E3A] text-xs font-medium hidden sm:flex items-center gap-1">
                    View Details <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            </div>
          )}

          {/* Showcase Opportunity Pages */}
          {showcaseOpps.length > 0 && (
            <div className="col-span-12 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5" data-testid="showcase-opportunities-section">
              <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
                <Megaphone className="w-4 h-4 text-[#C41E3A]" />
                Opportunities
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {showcaseOpps.map((opp) => (
                  <Link
                    key={opp.id}
                    to={`/p/${slug}/opportunity/${opp.slug}`}
                    className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 hover:border-[#C41E3A]/50 hover:bg-zinc-800 transition-all group"
                    data-testid={`showcase-opp-${opp.slug}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-semibold text-white group-hover:text-[#C41E3A] transition-colors line-clamp-2 text-sm">
                        {opp.title}
                      </h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#C41E3A]/10 text-[#C41E3A] whitespace-nowrap font-medium">
                        {(opp.category || '').replace(/_/g, ' ')}
                      </span>
                    </div>
                    {opp.overview && <p className="text-zinc-400 text-xs line-clamp-2 mb-3">{opp.overview}</p>}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                      {opp.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{opp.location}</span>}
                      {opp.key_dates?.deadline && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />Deadline: {new Date(opp.key_dates.deadline).toLocaleDateString()}</span>}
                      {opp.documents?.length > 0 && <span className="flex items-center gap-1"><FolderOpen className="w-3 h-3" />{opp.documents.length} doc{opp.documents.length > 1 ? 's' : ''}</span>}
                    </div>
                    <div className="flex items-center justify-end mt-3 pt-3 border-t border-zinc-700">
                      <span className="text-[#C41E3A] text-xs flex items-center gap-1 group-hover:gap-2 transition-all font-medium">
                        View Details <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Business Opportunities */}
          {opportunities.length > 0 && (
            <div className="col-span-12 bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5" data-testid="opportunities-section">
              <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
                <Link2 className="w-4 h-4 text-[#C41E3A]" />
                Business Opportunities
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {opportunities.map((opp, idx) => (
                  <Link 
                    key={idx}
                    to={`/connect?opp=${opp.id}`}
                    className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 hover:border-[#C41E3A]/50 hover:bg-zinc-800 transition-all group"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-semibold text-white group-hover:text-[#C41E3A] transition-colors line-clamp-2">
                        {opp.title}
                      </h3>
                      <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-300 whitespace-nowrap">
                        {opp.category.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm text-zinc-400">
                      {opp.sector && <span>{opp.sector}</span>}
                      {opp.geography && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {opp.geography}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-700">
                      <span className="text-xs text-zinc-500">
                        {new Date(opp.created_at).toLocaleDateString()}
                      </span>
                      <span className="text-[#C41E3A] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        View <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-4 text-center">
                <Link 
                  to="/connect" 
                  className="text-sm text-[#C41E3A] hover:underline inline-flex items-center gap-1"
                >
                  View all opportunities on Connect <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            </div>
          )}
          
          {/* Contact Card */}
          <div className={`col-span-12 ${profile.thought_leadership_topics?.length ? 'md:col-span-5' : 'md:col-span-12'} bg-gradient-to-br from-[#C41E3A]/20 to-zinc-900 border border-[#C41E3A]/30 rounded-2xl p-5 md:p-6`}>
            <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
              <Mail className="w-4 h-4 text-[#C41E3A]" />
              Connect
            </h2>
            
            <div className="space-y-3 mb-5">
              {profile.contact_email && (
                <a href={`mailto:${profile.contact_email}`} className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm transition-colors">
                  <Mail className="w-4 h-4 text-zinc-500" />
                  {profile.contact_email}
                </a>
              )}
              {profile.contact_phone && (
                <a href={`tel:${profile.contact_phone}`} className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm transition-colors">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  {profile.contact_phone}
                </a>
              )}
              {profile.linkedin_url && (
                <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm transition-colors">
                  <Linkedin className="w-4 h-4 text-zinc-500" />
                  LinkedIn Profile
                </a>
              )}
              {profile.twitter_url && (
                <a href={profile.twitter_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm transition-colors">
                  <Twitter className="w-4 h-4 text-zinc-500" />
                  Twitter
                </a>
              )}
            </div>
            
            {/* CTA Buttons */}
            <div className="space-y-2">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-green-600 hover:bg-green-500 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors text-sm"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                Chat on WhatsApp
              </a>
              <a
                href={emailUrl}
                className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors text-sm"
                data-testid="contact-email-btn"
              >
                <Mail className="w-4 h-4" />
                {categoryConfig.cta}
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-6">
        <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-zinc-500 text-xs">
            Profile by {profile.leader_name}. Information not verified by BrandStage.
          </p>
          <Link to="/" className="text-zinc-400 hover:text-white text-xs flex items-center gap-2">
            Powered by <img src={LOGO_URL} alt="BrandStage" className="h-5 w-auto rounded" />
          </Link>
        </div>
      </footer>

      {/* Mobile Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur-xl border-t border-zinc-800 p-3 md:hidden z-50">
        <div className="flex items-center gap-2">
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="flex-1 bg-green-600 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 text-sm">
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
          <a href={emailUrl} className="flex-1 bg-[#C41E3A] text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 text-sm">
            <Mail className="w-4 h-4" />
            Email
          </a>
          <button onClick={copyLink} className={`p-2.5 rounded-lg ${copied ? 'bg-green-600' : 'bg-zinc-800'} text-white`}>
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
